/**
 * チャット通知先 (chat_endpoints) の hooks (v3.2.21+)。
 * 種類は Google Chat と Chatwork (2026-10-26 に Chatwork を追加)。
 *
 * 読込: Firestore 直 (rules で editor+ 許可)
 * 書込: Callable (upsertChatEndpoint / deleteChatEndpoint)
 */
import type { ChatEndpoint, ChatEndpointKind } from '@goenhub/shared';
import { chatEndpointKind } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface ChatEndpointRecord extends ChatEndpoint {
  id: string;
}

/**
 * 通知先の一覧。kind を渡すとその種類だけ返す
 * (Google Chat の選択欄に Chatwork を出さない等)。kind 未設定の既存データは Google Chat。
 */
export function useChatEndpoints(kind?: ChatEndpointKind) {
  return useQuery({
    queryKey: ['chat_endpoints'],
    queryFn: async (): Promise<ChatEndpointRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'chat_endpoints'), orderBy('createdAt', 'asc')),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ChatEndpoint),
      }));
    },
    select: kind
      ? (rows: ChatEndpointRecord[]) => rows.filter((r) => chatEndpointKind(r) === kind)
      : undefined,
  });
}

export interface UpsertChatEndpointInput {
  id?: string | null;
  kind: ChatEndpointKind;
  name: string;
  webhookUrl: string;
  roomId?: string | null;
  description?: string | null;
  isActive: boolean;
}

const upsertFn = httpsCallable<UpsertChatEndpointInput, { id: string }>(
  functions,
  'upsertChatEndpoint',
);

export function useUpsertChatEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertChatEndpointInput) => {
      const r = await upsertFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat_endpoints'] }),
  });
}

const deleteFn = httpsCallable<{ id: string }, { id: string }>(functions, 'deleteChatEndpoint');

export function useDeleteChatEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await deleteFn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat_endpoints'] }),
  });
}
